
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/reagents/FormField";
import CategorySelect from "@/components/reagents/CategorySelect";
import DescriptionField from "@/components/reagents/DescriptionField";
import AmountInput from "@/components/reagents/AmountInput";
import ImageUploadField from "@/components/reagents/ImageUploadField";
import { Reagent } from "@/types/reagent.types";

interface EditReagentFormProps {
  formData: {
    name: string;
    catalogNumber: string;
    category: string;
    description: string;
    amount: string;
    unit: string;
    storageConditions: string;
    numberOfPackages: string;
  };
  reagent: Reagent | null;
  selectedImage: File | null;
  isSubmitting: boolean;
  categories: Array<{id: string, name: string}>;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onImageChange: (file: File | null) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (name: string, value: string) => void;
}

const EditReagentForm: React.FC<EditReagentFormProps> = ({
  formData,
  reagent,
  selectedImage,
  isSubmitting,
  categories,
  onCancel,
  onSubmit,
  onImageChange,
  onChange,
  onSelectChange
}) => {
  // Create a handler to convert ChangeEvent to File
  const handleImageUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onImageChange(e.target.files[0]);
    } else {
      onImageChange(null);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Edit Reagent Details</CardTitle>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField
            id="name"
            label="Name"
            value={formData.name}
            onChange={onChange}
            placeholder="e.g., Sodium Chloride"
            required
          />
          
          <FormField
            id="catalogNumber"
            label="Catalog Number"
            value={formData.catalogNumber}
            onChange={onChange}
            placeholder="e.g., S7653"
            required
          />
          
          <CategorySelect
            value={formData.category}
            onChange={(value) => onSelectChange("category", value)}
            categories={categories}
          />
          
          <AmountInput
            amount={formData.amount}
            unit={formData.unit}
            onAmountChange={(value) => onSelectChange("amount", value)}
            onUnitChange={(value) => onSelectChange("unit", value)}
          />
          
          <FormField
            id="numberOfPackages"
            label="Number of Packages"
            value={formData.numberOfPackages}
            onChange={onChange}
            placeholder="e.g., 1, 2, 3"
            type="number"
          />
          
          <DescriptionField
            value={formData.description}
            onChange={onChange}
          />
          
          <FormField
            id="storageConditions"
            label="Storage Conditions"
            value={formData.storageConditions}
            onChange={onChange}
            placeholder="e.g., 4°C, -20°C, Room temperature"
          />
          
          <div className="space-y-2">
            {reagent && (reagent.imageUrl || reagent.image_url) && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Current Image:</p>
                <img 
                  src={reagent.imageUrl || reagent.image_url} 
                  alt={reagent.name} 
                  className="w-40 h-40 object-cover rounded-md border border-gray-200"
                />
              </div>
            )}
            
            <ImageUploadField onChange={handleImageUploadChange} />
            {selectedImage && (
              <p className="text-sm text-green-600">
                New image selected: {selectedImage.name}
              </p>
            )}
          </div>
          
          <div className="flex justify-end space-x-4 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            
            <Button 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default EditReagentForm;
